import React, { useState, useMemo, useCallback } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { getUserToken } from '@/redux/user';
import { useSelector } from 'react-redux';
import { StepData } from '@/components/atoms/Wizard/Wizard';
import { computeText } from '@/locales/utils';
import { useIntl } from 'react-intl';
import { ENV } from '@/utils/env';
import { streamingFetch } from '@/utils/streamingFetch';
import { UploadStep } from './UploadStep';
import { ResultStep } from './ResultStep';
import { LoadingStep } from './LoadingStep';

type ImportCustomersFormValues = {
    file: File | null;
};

type CustomerErrorOrWarn = {
    line: number;
    email: string;
    invalidFields: string[];
};

type ImportCustomersResult = {
    customersCreated: number;
    customersUpdated: number;
    customersUnchanged: number;
    customersError: CustomerErrorOrWarn[];
    customersWarn: CustomerErrorOrWarn[];
    otherWarnings: Array<{ type: string; list: string[] }>;
    validationErrors: Array<{ lineNumber: number; error: string }>;
};

type ImportErrorDetail = {
    lineNumber: number;
    error: string;
    line: {
        firstName?: string;
        lastName?: string;
        email?: string;
        [key: string]: unknown;
    };
};

type ImportError = {
    message: string;
    errors: ImportErrorDetail[];
};

type ImportModalVMProps = {
    onHide: () => void;
    brandId: number;
    onImportSuccess?: () => void;
};

export const useImportCustomersModalVM = ({ onHide, brandId, onImportSuccess }: ImportModalVMProps) => {
    const intl = useIntl();
    const token = useSelector(getUserToken);
    const [currentStep, setCurrentStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [importResult, setImportResult] = useState<ImportCustomersResult | null>(null);
    const [error, setError] = useState<ImportError | null>(null);

    const uploadState = useMemo(() => (error ? 'ERROR' : 'SUCCESS'), [error]);

    const errorDetails = useMemo(() => error?.errors ?? [], [error]);

    const form: UseFormReturn<ImportCustomersFormValues> = useForm<ImportCustomersFormValues>({
        mode: 'all',
        defaultValues: {
            file: null,
        },
    });

    const importCustomers = async (file: File): Promise<ImportCustomersResult> => {
        const formData = new FormData();
        formData.append('file', file);

        const url = `${ENV.API_URL}/customers/import?brandId=${brandId}`;

        setProgress(0);

        const result = await streamingFetch<ImportCustomersResult>({
            url,
            headers: { authorization: `Bearer ${token}` },
            body: formData,
            onProgress: setProgress,
        });

        if (result.error) {
            throw Object.assign(new Error(result.error.message), { errors: result.error.errors });
        }

        if (!result.data) {
            throw new Error(computeText(intl, 'customer.modal.import.error.invalidResponse'));
        }

        return result.data;
    };

    const onFormValidate = async () => {
        const { getValues } = form;
        const { file } = getValues();
        if (!file) {
            return;
        }

        setIsLoading(true);
        setError(null);

        setCurrentStep(1); // Move to loading step

        try {
            const result = await importCustomers(file);
            setImportResult(result);
        } catch (err: unknown) {
            const errWithErrors = err as { message?: string; errors?: ImportErrorDetail[] };
            setError({
                message: errWithErrors.message || 'An error occurred',
                errors: errWithErrors.errors ?? [],
            });
        } finally {
            setIsLoading(false);
            setCurrentStep(2); // Move to result step
        }
    };

    const handleHide = useCallback(() => {
        if (isLoading) return true;

        if (importResult && onImportSuccess) {
            onImportSuccess();
        }

        setCurrentStep(0);
        setImportResult(null);
        setError(null);
        form.reset();
        onHide();
        return true;
    }, [isLoading, onHide, importResult, form, onImportSuccess]);

    const steps: StepData[] = [
        {
            title: 'customer.modal.import.report.title',
            component: <UploadStep brandId={brandId} />,
            showHeader: false,
            showNext: false,
            showValidate: true,
            onValidate: onFormValidate,
        },
        {
            title: 'customer.modal.import.report.title',
            component: <LoadingStep progress={progress} />,
            showHeader: false,
            showValidate: false,
            showPrevious: false,
        },
        {
            title: 'customer.modal.import.report.title',
            component: <ResultStep state={uploadState} errorDetails={errorDetails} importResult={importResult} />,
            showHeader: true,
            showValidate: false,
            showPrevious: false,
            cancelLabel: computeText(intl, 'button.ok'),
            onCancel: handleHide,
        },
    ];

    React.useEffect(() => {
        form.watch(['file']);
    }, [form]);

    return {
        form,
        canValidate: form.formState.isValid,
        currentStep,
        steps,
        handleHide,
        isLoading,
        onFormValidate,
    };
};
